import {
  addItemAndLink,
  dissociateItemFromBoard,
  getBoardsForCurrentUser,
  createBoard,
  joinBoard,
  leaveBoard,
} from '@/clients/supabase'
import {
  addItemToBoards,
  deleteItemFromBoard,
  type BoardItem,
  type BoardItemContextType,
  type Boards,
  buildBoard,
  addBoard,
  mapDbBoards,
  type Board,
} from '@/utils/moodboard'
import type { AuthStore } from './auth'
import { SYSTEM_BOARD_TITLE } from '@/utils/moodboard/constants'
import type { AlpineExtended } from '@/entrypoint-alpine'
import {
  sendCustomMoodboardEvent,
  sendSaveMoodboardEvents,
} from '@/utils/moodboard/event-helpers'

export interface MoodboardStore {
  ready: boolean
  loading: boolean
  boards: Boards
  addItemToDefaultBoard: (item: BoardItem) => void
  addItemToBoard: (item: BoardItem, boardId: string) => Promise<void>
  removeItemFromBoard: (item: BoardItem, boardId: string) => Promise<void>
  isSaved: (id: string, context: BoardItemContextType) => boolean
  isSavedTo: (
    item: { id: string; context: BoardItemContextType },
    boardId: string,
  ) => boolean
  savedCount: number
  createBoard(title: string, items?: BoardItem[]): Promise<Board | undefined>
  joinBoard(id: string): Promise<void>
  getAndSetDbBoards(): Promise<void>
  setLoggedInBoards(): Promise<void>
  handleLeaveBoard(id: string): Promise<void>
  clearBoards(): void
  isJoined(id: string): boolean
  boardsCount: number
}

export const moodboardStore = (Alpine: AlpineExtended): MoodboardStore => ({
  ready: false,
  loading: false,
  boards: Alpine.$persist(addBoard({}, buildBoard(SYSTEM_BOARD_TITLE))),

  isJoined(id: string): boolean {
    return this.boards[id] ? true : false
  },

  isSaved(id, context): boolean {
    const boards = this.boards
    if (!boards) return false

    for (const key in boards) {
      const board = boards[key]
      if (!board?.items) continue

      for (const item of board.items) {
        if (item.id === id && item.context === context) {
          return true
        }
      }
    }
    return false
  },

  isSavedTo(item, boardId): boolean {
    if (!item || !boardId) return false
    const { id, context } = item
    const board = this.boards?.[boardId]
    if (!board?.items?.length) return false

    let saved = false
    for (const item of board.items) {
      if (item.id === id && item.context === context) {
        saved = true
        break
      }
    }
    return saved
  },

  get savedCount(): number {
    const boards = this.boards
    if (!boards) return 0
    let count = 0
    for (const key in boards) {
      const board = boards[key]
      if (!board?.items) continue
      count += board.items.length
    }
    return count
  },

  get boardsCount(): number {
    return this.boards && this.savedCount ? Object.keys(this.boards).length : 0
  },

  async createBoard(title, items) {
    this.loading = true
    try {
      const authenticated = (Alpine.store('auth') as AuthStore).isLoggedIn
      if (!authenticated) return

      const board = buildBoard(title, items)
      if (!board) throw new Error('Failed to build board')
      await createBoard(board)

      this.boards = addBoard(this.boards, board)

      sendCustomMoodboardEvent('moodboard_board_created', {
        board_title: title,
        board_id: board.id,
        authenticated,
      })
      return board
    } catch (error) {
      console.error('[store.moodboard.createBoard]:', error)
    } finally {
      this.loading = false
    }
  },

  async joinBoard(id) {
    this.loading = true
    try {
      await joinBoard(id)
      this.getAndSetDbBoards()
      sendCustomMoodboardEvent('moodboard_join_success', {
        board_id: id,
        authenticated: true,
      })
    } catch (error) {
      console.error('[store.moodboard.joinBoard]:', error)
    } finally {
      this.loading = false
    }
  },

  async getAndSetDbBoards() {
    try {
      const boards = await getBoardsForCurrentUser()
      if (!boards?.length) {
        this.clearBoards()
        return
      }
      this.boards = mapDbBoards(boards)
    } catch (error) {
      console.error('Error fetching board:', error)
    }
  },

  addItemToDefaultBoard(item) {
    if (!this.boards) return
    const allBoards = Object.values(this.boards)
    const defaultBoard = allBoards.find(
      (board) => board.title === SYSTEM_BOARD_TITLE,
    )
    if (!defaultBoard) return
    this.addItemToBoard(item, defaultBoard.id)
  },

  async addItemToBoard(item, boardId) {
    this.loading = true
    try {
      if (!boardId) throw new Error('No board ID available')
      const authenticated = (Alpine.store('auth') as AuthStore).isLoggedIn

      if (authenticated) {
        await addItemAndLink(boardId, item)
      }

      this.boards = addItemToBoards(this.boards, item, boardId)
      sendSaveMoodboardEvents('add', item, { board_id: boardId, authenticated })
    } catch (error) {
      console.error('[store.moodboard.addItemToBoard]:', error)
    } finally {
      this.loading = false
    }
  },

  async removeItemFromBoard(item, boardId) {
    this.loading = true
    try {
      const itemUid = `${item.id}::${item.context}`
      const authenticated = (Alpine.store('auth') as AuthStore).isLoggedIn
      if (authenticated) {
        await dissociateItemFromBoard(boardId, itemUid)
      }

      this.boards = deleteItemFromBoard(this.boards, item, boardId)
      sendSaveMoodboardEvents('remove', item, {
        board_id: boardId,
        authenticated,
      })
    } catch (error) {
      console.error('[store.moodboard.removeItemFromBoard]:', error)
    } finally {
      this.loading = false
    }
  },

  async setLoggedInBoards() {
    if (!(Alpine.store('auth') as AuthStore).isLoggedIn) return

    // This should only ever be one board: the system local board
    for (const boardId in this.boards) {
      const board = this.boards[boardId]
      try {
        await createBoard(board)
        for (const item of board.items) {
          await addItemAndLink(board.id, item)
        }
      } catch (error) {
        console.error('[store.moodboard.persistLocalBoardsOnLogin]:', error)
        break
      } finally {
        this.getAndSetDbBoards()
      }
    }
  },

  async handleLeaveBoard(id: string) {
    try {
      const updatedBoards = { ...this.boards }
      if (!updatedBoards[id]) return
      delete updatedBoards[id]
      this.boards = updatedBoards

      if ((Alpine.store('auth') as AuthStore).isLoggedIn) {
        await leaveBoard(id)
        this.getAndSetDbBoards()

        sendCustomMoodboardEvent('moodboard_board_left', {
          board_id: id,
          authenticated: true,
        })
        return
      }

      if (!this.boards || !Object.keys(this.boards)?.length) {
        this.clearBoards()
      }
    } catch (error) {
      console.error('[store.moodboard.handleLeaveBoard]:', error)
    }
  },

  clearBoards() {
    const defaultBoard = buildBoard(SYSTEM_BOARD_TITLE)
    this.boards = addBoard(null, defaultBoard)
  },
})
